import { useState } from 'react';

import { SettingsEditorsEndpoint } from '@core/types/types.endpoint.settings';
import { Button } from '@frontend/components/ui/button';
import { UserField } from '@frontend/shared/components/UserField/UserField';
import { useDirectoryUpdater } from '@frontend/shared/hooks/shared.hook.useDirectoryUpdater';
import {
  useDirectoryTeam,
  useDirectoryUsers,
} from '@frontend/shared/hooks/useDirectory';
import { useSubmitter } from '@frontend/shared/hooks/useSubmitter';
import { callApi } from '@frontend/shared/util/util.callApi';

interface SettingsEditorsProps {
  isReadOnly?: boolean;
}

export function SettingsEditors({ isReadOnly = false }: SettingsEditorsProps) {
  const users = useDirectoryUsers();
  const team = useDirectoryTeam();
  const updater = useDirectoryUpdater();

  // Get current editor IDs from team data
  const editorIds = users
    .filter((user) => team.editors.includes(user.id))
    .map(({ id }) => id);

  const [selectedIds, setSelectedIds] = useState(editorIds);

  const isDifferent =
    editorIds.length !== selectedIds.length ||
    selectedIds.filter((id) => !editorIds.includes(id)).length > 0;

  const submitter = useSubmitter(async () => {
    const [, res] = await callApi<SettingsEditorsEndpoint>(
      '/settings/editors',
      {
        editorIds: selectedIds,
      },
    );
    if (res) {
      updater({
        team: res,
      });
    }
  });

  return (
    <div>
      <h3 className="text-xs font-semibold">Editors</h3>
      <p className="mt-1 text-xs font-medium text-gray-500">
        Can create, edit, and manage answers. Editors receive a{' '}
        <a
          href="https://www.questionbase.com/resources/docs/how-does-the-to-do-list-case-tracker-work"
          target="_blank"
          rel="noopener noreferrer"
          className="underline"
        >
          Case Tracker
        </a>
        .
      </p>
      {!isReadOnly && (
        <div className="max-w-[540px]">
          <UserField
            isDisabled={submitter.isSubmitting}
            placeholder="+Add Editor"
            excludeDeleted
            selectedIds={selectedIds}
            setSelectedIds={setSelectedIds}
          />
        </div>
      )}
      {isReadOnly && (
        <div className="mt-2">
          {editorIds.map((id) => {
            const user = users.find((u) => u.id === id);
            return user ? (
              <p key={id} className="mb-1 text-sm">
                {user.name}
              </p>
            ) : null;
          })}
        </div>
      )}

      {!isReadOnly && isDifferent && (
        <div className="mt-4 flex">
          <Button
            size="sm"
            disabled={!selectedIds.length || submitter.isSubmitting}
            onClick={submitter.submit}
          >
            {submitter.isSubmitting ? 'Saving...' : 'Save'}
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="ml-2"
            onClick={() => setSelectedIds(editorIds)}
          >
            Cancel
          </Button>
        </div>
      )}
    </div>
  );
}
